// src/pages/TestCatalogPage.jsx
import { AddIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Badge, Box, Button, Card, CardBody, Checkbox, Divider, Flex, FormControl, FormErrorMessage,
    Heading, HStack, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Select,
    Spinner, Switch, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure,
    useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FiMoreVertical, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { hasPermission } from '../../../Utils';
import { createTest, deleteTest, getTestCategories, getTests, restoreTest, updateTest } from '../../../api';
import TestRequisitionBuilder from './TestRequisitionBuilder';

const PageLimit = 20;
const CPT_RX = /^[A-Z0-9]{5,7}$/;
const CODE_RX = /^[A-Z0-9][A-Z0-9_\-]{1,39}$/i;

const SPECIMEN_TYPES = [
    'Serum', 'Plasma', 'Whole Blood', 'Urine', 'Stool', 'Cerebrospinal Fluid (CSF)',
    'Tissue (FFPE)', 'Tissue (Fresh)', 'Swab', 'Saliva', 'Sputum', 'Synovial Fluid',
    'Pleural Fluid', 'Ascitic Fluid', 'Semen', 'Hair', 'Nail'
];

const asArray = (v) => Array.isArray(v) ? v : String(v || '').split(/[,\s]+/).filter(Boolean);

export default function TestCatalogPage() {
    const toast = useToast();

    const canView = hasPermission('viewTestCatalog');
    const canCreate = hasPermission('createTestCatalog');
    const canUpdate = hasPermission('updateTestCatalog');
    const canDelete = hasPermission('deleteTestCatalog');
    const canRestore = hasPermission('restoreTestCatalog');

    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [q, setQ] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [cptFilter, setCptFilter] = useState('');
    const [codeFilter, setCodeFilter] = useState('');
    const [numberFilter, setNumberFilter] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [sort, setSort] = useState('displayOrder,name');

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);

    const [builderOpen, setBuilderOpen] = useState(false);

    const [form, setForm] = useState({
        name: '', code: '', number: '', categoryId: '', description: '',
        cpts: '', loinc: '', specimenType: '', specimenContainer: '',
        specimenVolume: '', specimenTemperature: '', collectionInstructions: '',
        tat: '', requisitionSchema: '', displayOrder: 0, isActive: true,
    });
    const [errors, setErrors] = useState({});
    const [showErrors, setShowErrors] = useState(false);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        p.set('page', String(page));
        p.set('limit', String(PageLimit));
        if (q) p.set('q', q);
        if (categoryFilter) p.set('categoryId', categoryFilter);
        if (cptFilter) p.set('cpt', cptFilter);
        if (codeFilter) p.set('code', codeFilter);
        if (numberFilter) p.set('number', numberFilter);
        if (isActiveFilter) p.set('isActive', isActiveFilter);
        if (includeDeleted) p.set('includeDeleted', 'true');
        if (sort) p.set('sort', sort);
        return p.toString();
    }, [page, q, categoryFilter, cptFilter, codeFilter, numberFilter, isActiveFilter, includeDeleted, sort]);

    const loadCats = async () => {
        try {
            const res = await getTestCategories('limit=200&sort=displayOrder,name');
            const payload = res?.data || res;
            setCats((payload.items || []).filter(x => !x.deletedAt));
        } catch { /* noop */ }
    };

    const load = async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const res = await getTests(decodeURIComponent(queryString));
            if (res.success) {
                const payload = res?.data || res;
                setItems(payload.items || []);
                setTotal(payload.total || 0);
                setPage(payload.page || 1);
                setPages(payload.pages || 1);
            } else {
                setError(res.error || res.message);
            }
        } catch (err) {
            toast({ status: 'error', title: 'Load failed', description: err.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadCats(); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [queryString, canView]);

    const resetForm = () => {
        setForm({
            name: '',
            code: '',
            number: '',
            categoryId: cats[0]?._id || '',
            description: '',
            cpts: '',
            loinc: '',
            specimenType: '',
            specimenContainer: '',
            specimenVolume: '',
            specimenTemperature: '',
            collectionInstructions: '',
            tat: '',
            requisitionSchema: '',
            displayOrder: 0,
            isActive: true,
        });
        setErrors({});
        setShowErrors(false);
        setEditing(null);
    };

    const openCreate = () => {
        if (!canCreate) return toast({ status: 'warning', title: 'No permission to create' });
        resetForm(); onOpen();
    };

    const openEdit = (doc) => {
        if (!canUpdate) return toast({ status: 'warning', title: 'No permission to edit' });
        setEditing(doc);
        setForm({
            name: doc.name || '',
            code: doc.code || '',
            number: String(doc.number ?? ''),
            categoryId: doc.categoryId || '',
            description: doc.description || '',
            cpts: asArray(doc.cpts).join(', '),
            loinc: doc.loinc || '',
            specimenType: doc.defaultSpecimen?.type || '',
            specimenContainer: doc.defaultSpecimen?.container || '',
            specimenVolume: doc.defaultSpecimen?.volume || '',
            specimenTemperature: doc.defaultSpecimen?.temperature || '',
            collectionInstructions: doc.collectionInstructions || '',
            tat: doc.tat || '',
            requisitionSchema: doc.requisitionSchema ? JSON.stringify(doc.requisitionSchema, null, 2) : '',
            displayOrder: typeof doc.displayOrder === 'number' ? doc.displayOrder : 0,
            isActive: !!doc.isActive,
        });
        setErrors({});
        setShowErrors(false);
        onOpen();
    };

    // submit-only validation
    const normalized = (f) => ({
        ...f,
        code: String(f.code || '').toUpperCase(),
        cpts: String(f.cpts || '').toUpperCase(),
        loinc: String(f.loinc || '').toUpperCase(),
    });

    const validate = (f0) => {
        const f = normalized(f0);
        const e = {};
        if (!f.name || f.name.trim().length < 2) e.name = 'Name is required';
        if (!f.code || !CODE_RX.test(f.code)) e.code = 'Code required (2–40 alnum/_/-)';
        if (!f.number || !/^\d+$/.test(String(f.number))) e.number = 'Number must be numeric';
        if (!f.categoryId) e.categoryId = 'Category is required';

        const codes = String(f.cpts || '').split(/[,\s]+/).filter(Boolean);
        for (const c of codes) { if (!CPT_RX.test(c)) { e.cpts = 'CPTs must be 5–7 alphanumeric chars'; break; } }

        if (f.requisitionSchema) { try { JSON.parse(f.requisitionSchema); } catch { e.requisitionSchema = 'Invalid JSON'; } }
        return e;
    };

    const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));

    const onSave = async () => {
        const f = normalized(form);
        const e = validate(f);
        setErrors(e); setShowErrors(true);
        if (Object.keys(e).length) { toast({ status: 'error', title: 'Please fix form errors' }); return; }

        try {
            setSaving(true);
            const payload = {
                name: f.name.trim(),
                code: f.code.trim(),
                number: Number(f.number),
                categoryId: f.categoryId,
                description: f.description.trim(),
                cpts: f.cpts,
                loinc: f.loinc.trim(),
                defaultSpecimen: (f.specimenType || f.specimenContainer || f.specimenVolume || f.specimenTemperature)
                    ? { type: f.specimenType.trim(), container: f.specimenContainer.trim(), volume: f.specimenVolume.trim(), temperature: f.specimenTemperature.trim() }
                    : null,
                collectionInstructions: f.collectionInstructions.trim(),
                tat: f.tat.trim(),
                requisitionSchema: f.requisitionSchema ? JSON.parse(f.requisitionSchema) : null,
                displayOrder: Number(f.displayOrder) || 0,
                isActive: !!f.isActive,
            };

            const res = editing ? await updateTest(editing._id, payload) : await createTest(payload);
            if (res?.success === false) throw new Error(res?.message || 'Save failed');

            toast({ status: 'success', title: editing ? 'Updated' : 'Created' });
            onClose(); resetForm(); load();
        } catch (err) {
            toast({ status: 'error', title: 'Save failed', description: err.message });
        } finally {
            setSaving(false);
        }
    };

    const onSoftDelete = async (id) => {
        if (!canDelete) return toast({ status: 'warning', title: 'No permission to delete' });
        try {
            setDeletingId(id);
            const res = await deleteTest(id, 'User requested removal');
            if (res?.success === false) throw new Error(res?.message || 'Delete failed');
            toast({ status: 'success', title: 'Soft deleted' }); load();
        } catch (err) { toast({ status: 'error', title: 'Delete failed', description: err.message }); }
        finally { setDeletingId(null); }
    };

    const onRestore = async (id) => {
        if (!canRestore) return toast({ status: 'warning', title: 'No permission to restore' });
        try {
            setRestoringId(id);
            const res = await restoreTest(id);
            if (res?.success === false) throw new Error(res?.message || res?.error || 'Restore failed');
            toast({ status: 'success', title: 'Restored' }); load();
        } catch (err) { toast({ status: 'error', title: 'Restore failed', description: err.message }); }
        finally { setRestoringId(null); }
    };

    if (!canView) return <Box p={5}><Text>You do not have permission to view tests.</Text></Box>;
    if (error) return <Box p={5}><Text color="red.500">{error}</Text></Box>;

    return (
        <Box p={5}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Test Catalog</Heading>
                <HStack>
                    {canCreate && <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>New Test</Button>}
                    <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={load} />
                </HStack>
            </Flex>

            {/* Filters */}
            <Card mb={4}>
                <CardBody>
                    <HStack spacing={3} flexWrap="wrap">
                        <Input placeholder="Search..." value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} maxW="280px" />
                        <Select value={categoryFilter} onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }} maxW="260px">
                            <option value="">All categories</option>
                            {cats.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </Select>
                        <Input placeholder="Filter by CPT" value={cptFilter} onChange={(e) => { setPage(1); setCptFilter(e.target.value.toUpperCase()); }} maxW="160px" />
                        <Input placeholder="Filter by Code" value={codeFilter} onChange={(e) => { setPage(1); setCodeFilter(e.target.value); }} maxW="160px" />
                        <Input placeholder="Filter by Number" value={numberFilter} onChange={(e) => { setPage(1); setNumberFilter(e.target.value); }} maxW="160px" />
                        <Select value={isActiveFilter} onChange={(e) => { setPage(1); setIsActiveFilter(e.target.value); }} maxW="160px">
                            <option value="">Any status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>
                        <Checkbox isChecked={includeDeleted} onChange={(e) => { setPage(1); setIncludeDeleted(e.target.checked); }}>
                            Include deleted
                        </Checkbox>
                        <Select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} maxW="260px">
                            <option value="displayOrder,name">Sort: Order, Name</option>
                            <option value="-createdAt">Newest</option>
                            <option value="name">Name A→Z</option>
                            <option value="-name">Name Z→A</option>
                        </Select>
                    </HStack>
                </CardBody>
            </Card>

            {/* Table */}
            <Card>
                <CardBody p={0}>
                    {loading ? (
                        <Flex align="center" justify="center" py={10}><Spinner /></Flex>
                    ) : (
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th w="36px">#</Th>
                                    <Th>Name</Th>
                                    <Th>Code</Th>
                                    <Th>Number</Th>
                                    <Th>Category</Th>
                                    <Th>CPTs</Th>
                                    <Th>TAT</Th>
                                    <Th>Status</Th>
                                    <Th w="120px">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {items.map((it, idx) => {
                                    const row = (page - 1) * PageLimit + idx + 1;
                                    const catName = cats.find(c => c._id === it.categoryId)?.name || '—';
                                    return (
                                        <Tr key={it._id} _hover={{ bg: 'gray.50' }}>
                                            <Td>{row}</Td>
                                            <Td>
                                                <Text fontWeight="semibold">{it.name || '-'}</Text>
                                                {it.description ? <Text fontSize="xs" color="gray.600" noOfLines={1}>{it.description}</Text> : null}
                                                {it.collectionInstructions ? <Text fontSize="xs" color="gray.500" noOfLines={1}>{it.collectionInstructions}</Text> : null}
                                            </Td>
                                            <Td>{it.code || '-'}</Td>
                                            <Td>{(it.number ?? '') === '' ? '—' : it.number}</Td>
                                            <Td>{catName}</Td>
                                            <Td><Text noOfLines={1} maxW="280px">{asArray(it.cpts).join(', ') || '—'}</Text></Td>
                                            <Td>{it.tat || '—'}</Td>
                                            <Td>{it.deletedAt ? <Badge colorScheme="red">Deleted</Badge> : (it.isActive ? <Badge colorScheme="green">Active</Badge> : <Badge>Inactive</Badge>)}</Td>
                                            <Td>
                                                <HStack spacing={1} justify="flex-end">
                                                    {!it.deletedAt ? (
                                                        <>
                                                            {canUpdate && (
                                                                <IconButton aria-label="edit" size="sm" icon={<EditIcon />} onClick={() => openEdit(it)} />
                                                            )}
                                                            {canDelete && (
                                                                <Menu>
                                                                    <MenuButton as={IconButton} aria-label="more" size="sm" icon={<FiMoreVertical />} />
                                                                    <MenuList>
                                                                        <MenuItem icon={<FiTrash2 />} onClick={() => onSoftDelete(it._id)} isDisabled={deletingId === it._id}>
                                                                            {deletingId === it._id ? 'Deleting…' : 'Soft delete'}
                                                                        </MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            )}
                                                        </>
                                                    ) : (
                                                        canRestore && (
                                                            <Button size="sm" leftIcon={<FiRotateCcw />} onClick={() => onRestore(it._id)} isLoading={restoringId === it._id}>
                                                                Restore
                                                            </Button>
                                                        )
                                                    )}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                                {!items.length && !loading && (
                                    <Tr><Td colSpan={9}><Flex justify="center" py={10}><Text color="gray.500">No tests</Text></Flex></Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Pagination */}
            <Flex align="center" justify="space-between" mt={4}>
                <Text fontSize="sm" color="gray.600">{total} total • Page {page} of {pages}</Text>
                <HStack>
                    <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Prev</Button>
                    <Button size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} isDisabled={page >= pages}>Next</Button>
                </HStack>
            </Flex>

            {/* Create / Edit Modal */}
            {isOpen && (
                <Box position="fixed" inset="0" bg="blackAlpha.500" display="flex" alignItems="center" justifyContent="center" zIndex={1000}>
                    <Card w="980px" maxW="96vw">
                        <CardBody>
                            <Heading size="md" mb={3}>{editing ? 'Edit Test' : 'New Test'}</Heading>
                            <VStack align="stretch" spacing={3}>
                                <HStack align="flex-end">
                                    <FormControl isInvalid={showErrors && !!errors.name} flex="1.1">
                                        <Text fontSize="sm" mb={1}>Name *</Text>
                                        <Input value={form.name} onChange={e => updateForm({ name: e.target.value })} />
                                        {showErrors && <FormErrorMessage>{errors.name}</FormErrorMessage>}
                                    </FormControl>

                                    <FormControl isInvalid={showErrors && !!errors.code} w="280px">
                                        <Text fontSize="sm" mb={1}>Code *</Text>
                                        <Input value={form.code} onChange={e => updateForm({ code: e.target.value.toUpperCase() })} placeholder="e.g., BIOPSY_HE" />
                                        {showErrors && <FormErrorMessage>{errors.code}</FormErrorMessage>}
                                    </FormControl>

                                    <FormControl isInvalid={showErrors && !!errors.number} w="280px">
                                        <Text fontSize="sm" mb={1}>Number *</Text>
                                        <Input type="number" value={form.number} onChange={e => updateForm({ number: e.target.value.replace(/[^\d]/g, '') })} placeholder="e.g., 10023" />
                                        {showErrors && <FormErrorMessage>{errors.number}</FormErrorMessage>}
                                    </FormControl>
                                </HStack>

                                <FormControl>
                                    <Text fontSize="sm" mb={1}>Description</Text>
                                    <Textarea rows={2} value={form.description} onChange={e => updateForm({ description: e.target.value })} />
                                </FormControl>

                                <HStack>
                                    <FormControl isInvalid={showErrors && !!errors.categoryId}>
                                        <Text fontSize="sm" mb={1}>Category *</Text>
                                        <Select value={form.categoryId} onChange={(e) => updateForm({ categoryId: e.target.value })}>
                                            <option value="">Select category</option>
                                            {cats.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                        </Select>
                                        {showErrors && <FormErrorMessage>{errors.categoryId}</FormErrorMessage>}
                                    </FormControl>

                                    <FormControl isInvalid={showErrors && !!errors.cpts}>
                                        <Text fontSize="sm" mb={1}>CPTs (comma/space)</Text>
                                        <Input value={form.cpts} onChange={e => updateForm({ cpts: e.target.value.toUpperCase() })} placeholder="88305, A4259, AWA13Z" />
                                        {showErrors && <FormErrorMessage>{errors.cpts}</FormErrorMessage>}
                                    </FormControl>

                                    <Box w="220px">
                                        <Text fontSize="sm" mb={1}>LOINC</Text>
                                        <Input value={form.loinc} onChange={e => updateForm({ loinc: e.target.value.toUpperCase() })} />
                                    </Box>
                                </HStack>

                                <HStack align="flex-end">
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Specimen Type</Text>
                                        <Input list="specimen-types" value={form.specimenType} onChange={e => updateForm({ specimenType: e.target.value })} placeholder="Start typing or pick…" />
                                        <datalist id="specimen-types">
                                            {SPECIMEN_TYPES.map(x => <option key={x} value={x} />)}
                                        </datalist>
                                    </Box>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Container</Text>
                                        <Input value={form.specimenContainer} onChange={e => updateForm({ specimenContainer: e.target.value })} />
                                    </Box>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Volume</Text>
                                        <Input value={form.specimenVolume} onChange={e => updateForm({ specimenVolume: e.target.value })} />
                                    </Box>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Temperature</Text>
                                        <Input value={form.specimenTemperature} onChange={e => updateForm({ specimenTemperature: e.target.value })} />
                                    </Box>
                                </HStack>

                                <Box>
                                    <Text fontSize="sm" mb={1}>Collection Instructions</Text>
                                    <Textarea value={form.collectionInstructions} onChange={e => updateForm({ collectionInstructions: e.target.value })} rows={2} />
                                </Box>

                                <HStack>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Turnaround Time (TAT)</Text>
                                        <Input value={form.tat} onChange={e => updateForm({ tat: e.target.value })} placeholder="e.g., 24–48h" />
                                    </Box>
                                    <Box w="180px">
                                        <Text fontSize="sm" mb={1}>Display Order</Text>
                                        <Input type="number" value={form.displayOrder} onChange={e => updateForm({ displayOrder: e.target.value })} />
                                    </Box>
                                    <HStack>
                                        <Text>Active</Text>
                                        <Switch isChecked={form.isActive} onChange={(e) => updateForm({ isActive: e.target.checked })} />
                                    </HStack>
                                </HStack>

                                <FormControl mt={4} style={{ borderTopWidth: 2, borderColor: "#ccc", paddingTop: 20 }} isInvalid={showErrors && !!errors.requisitionSchema}>
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontSize="sm">Requisition Schema (JSON, optional)</Text>
                                        <HStack>
                                            <Button size="xs" onClick={() => setBuilderOpen(true)}>Build visually</Button>
                                            <Button size="xs" variant="ghost" onClick={() => updateForm({ requisitionSchema: '' })}>Clear</Button>
                                        </HStack>
                                    </HStack>
                                    <Textarea value={form.requisitionSchema} onChange={e => updateForm({ requisitionSchema: e.target.value })} placeholder='{"schemaVersion":1,"fields":[...]}' rows={4} />
                                    {showErrors && <FormErrorMessage>{errors.requisitionSchema}</FormErrorMessage>}
                                </FormControl>

                                <TestRequisitionBuilder
                                    isOpen={builderOpen}
                                    onClose={() => setBuilderOpen(false)}
                                    initialSchema={form.requisitionSchema ? form.requisitionSchema : { schemaVersion: 1, title: 'REQUISITION', fields: [] }}
                                    onSave={(schemaObj) => {
                                        const json = JSON.stringify(schemaObj, null, 2);
                                        updateForm({ requisitionSchema: json });
                                    }}
                                />
                            </VStack>

                            <Divider my={4} />
                            <HStack justify="flex-end">
                                <Button variant="ghost" onClick={() => { onClose(); resetForm(); }} isDisabled={saving}>Cancel</Button>
                                <Button colorScheme="blue" onClick={onSave} isLoading={saving} isDisabled={(editing && !canUpdate) || (!editing && !canCreate)}>
                                    {editing ? 'Save Changes' : 'Create'}
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>
                </Box>
            )}
        </Box>
    );
}
